import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EarlyPayoutConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { NgxPopperjsContentComponent } from 'ngx-popperjs';

import { CashoutSettingsBetInfo, CashoutSettingsType, IAutoCashoutBetslipNumbers } from '../../../../my-bets-base/models/early-payout';
import { CashoutSettingsService } from '../../../services/cash-out-settings.service';

export interface Tab {
    tabName: string;
    index: number;
    indentifier: string;
    hasValue: boolean;
}

@Component({
    selector: 'ms-my-bets-earlypayout-popper-tabs',
    templateUrl: 'my-bets-earlypayout-popper-tabs.html',
    standalone: false,
})
export class MyBetsEarlyPayoutPopperTabsComponent implements OnInit {
    @Input() betNumbers: IAutoCashoutBetslipNumbers;
    @Input() closePopup: NgxPopperjsContentComponent;
    @Input() betTypeInfo: string;

    private betInfo: CashoutSettingsBetInfo | null = null;
    tabs: Tab[] = [];
    isAutoCashoutSet = false;
    isNotificationSet = false;
    selectedTabIndex = 0;
    typesMap = CashoutSettingsType;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private nativeAppService: NativeAppService,
        private deviceService: DeviceService,
        public sitecore: Sitecore,
        private csService: CashoutSettingsService,
        private epConfig: EarlyPayoutConfig,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.deviceService.orientation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onOrientationChange);
        this.betInfo = this.csService.getBetInfo(this.betNumbers.betslipId);
        this.setTabs();

        this.csService.settingsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((betid: string) => this.settingstValueChanged(betid));
    }

    private onOrientationChange(): void {
        if (this.closePopup && this.closePopup.hide) {
            this.closePopup.hide();
        }
    }

    private setTabs(): void {
        if (this.epConfig.isAutoCashOutEnabled || (this.betInfo && this.betInfo.earlyPayout!.autoCashOutValue)) {
            this.tabs.push({
                tabName: this.sitecore.earlyPayoutV2.SetAutocashoutTitleText,
                index: this.typesMap.Auto,
                indentifier: 'Auto',
                hasValue: false,
            });
            this.adjustSettingsStates();
        }

        if (
            this.nativeAppService.isNative &&
            (this.epConfig.isAutoCashOutNotificationEnabled || (this.betInfo && this.betInfo.earlyPayout!.autoCashoutNotificationValue))
        ) {
            this.tabs.push({
                tabName: this.sitecore.earlyPayoutV2.SetAutocashoutNotificationTitleText,
                index: this.typesMap.Notify,
                indentifier: 'Notify',
                hasValue: false,
            });
            this.adjustSettingsStates();
        }

        if (this.tabs.length) {
            this.selectedTabIndex = this.tabs[0].index;
        }
    }

    private adjustSettingsStates(): void {
        if (!this.betInfo) {
            return;
        }

        this.isAutoCashoutSet = this.betInfo.earlyPayout!.autoCashOutValue ? true : false;
        this.isNotificationSet = this.betInfo.earlyPayout!.autoCashoutNotificationValue ? true : false;
        const indexA = this.tabs.findIndex((el) => el.index === this.typesMap.Auto);
        const indexN = this.tabs.findIndex((el) => el.index === this.typesMap.Notify);

        if (indexA > -1) {
            this.tabs[indexA].hasValue = this.isAutoCashoutSet;
        }
        if (indexN > -1) {
            this.tabs[indexN].hasValue = this.isNotificationSet;
        }
    }

    settingstValueChanged(betId: string): void {
        if (this.betInfo && betId !== this.betNumbers.betslipId) {
            return;
        }
        this.betInfo = this.csService.getBetInfo(this.betNumbers.betslipId);
        this.checkIfTabHasToBeRemoved();
        this.adjustSettingsStates();
        this.changeRef.detectChanges();
    }

    private checkIfTabHasToBeRemoved(): void {
        if (!this.epConfig.isAutoCashOutEnabled && this.betInfo && !this.betInfo.earlyPayout!.autoCashOutValue) {
            this.spliceTabs('Auto');
        }

        if (!this.epConfig.isAutoCashOutNotificationEnabled && this.betInfo && !this.betInfo.earlyPayout!.autoCashoutNotificationValue) {
            this.spliceTabs('Notify');
        }
    }

    tabClicked(tab: Tab): void {
        if (this.selectedTabIndex === tab.index) {
            return;
        }

        this.selectedTabIndex = tab.index;
    }

    private spliceTabs(identity: string): void {
        const index = this.tabs.findIndex((el) => el.indentifier === identity);
        if (index > -1) {
            this.selectedTabIndex = 0;

            if (this.tabs.length === 1) {
                this.onOrientationChange();
            } else {
                this.tabs.splice(index, 1);
            }
        }
    }
}
